import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Clock } from "lucide-react";

import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSkeleton from "@/components_v2/Skeleton/BaseSkeleton";
import BaseSkeletonGroup from "@/components_v2/Skeleton/BaseSkeletonGroup";
import { SKELETON_VARIANTS } from "@/components_v2/Skeleton/constants";
import {
  getIncorrectQuestionNumbers,
  getLatestAttempt,
  getPassMarkPercent,
  getQuestionCount,
} from "@/domains/certifications/knowledgeCheck";
import KnowledgeCheckModal from "@/features/certifications/module/components/KnowledgeCheckModal";
import RequirementBadge from "@/features/certifications/module/components/RequirementBadge";
import { KnowledgeCheckCardProps } from "@/features/certifications/module/components/types";
import { KNOWLEDGE_CHECK_STATES } from "@/domains/certifications/constants";

import "./styles.css";

const ICON_SIZE = 18;

const KnowledgeCheckCard = ({
  certificationSlug,
  isLocked,
  weekPosition,
  check,
  state,
  isError,
}: KnowledgeCheckCardProps) => {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!check || !state) {
    return (
      <section className="certification-card module-card module-card-body flex flex-col gap-400">
        {isError ? (
          <p className="certification-muted body-s-regular" role="alert">
            {t("certification.module.check.loadError")}
          </p>
        ) : (
          <BaseSkeletonGroup
            label={t("labels.loading")}
            className="flex flex-col gap-400"
          >
            <BaseSkeleton width="10rem" height="var(--spacing-400)" />

            <BaseSkeleton width="70%" height="var(--spacing-500)" />

            <BaseSkeleton variant={SKELETON_VARIANTS.TEXT} lines={2} />
          </BaseSkeletonGroup>
        )}
      </section>
    );
  }

  const latest = getLatestAttempt(check);
  const incorrect =
    state === KNOWLEDGE_CHECK_STATES.ANOTHER_ROUND && latest
      ? getIncorrectQuestionNumbers(check, latest)
      : [];
  const isOpen = check.isActive && !isLocked;
  const canRetake = state === KNOWLEDGE_CHECK_STATES.ANOTHER_ROUND && isOpen;
  const canStart = state === KNOWLEDGE_CHECK_STATES.NOT_STARTED && isOpen;

  const stats = [
    {
      key: "questions",
      label: t("certification.module.check.questions"),
      value: t("certification.module.check.questionsValue", {
        count: getQuestionCount(check),
      }),
    },
    {
      key: "passMark",
      label: t("certification.module.check.passMark"),
      value: t("certification.module.check.percent", {
        percent: getPassMarkPercent(check),
      }),
    },
    {
      key: "attempts",
      label: t("certification.module.check.attempts"),
      value: t("certification.module.check.attemptsValue", {
        used: check.attempts.length,
        max: check.maxAttempts,
      }),
    },
  ];
  const isStatusTag =
    state !== KNOWLEDGE_CHECK_STATES.NOT_STARTED &&
    state !== KNOWLEDGE_CHECK_STATES.UNDER_REVIEW;

  return (
    <section className="certification-card module-card">
      <div className="module-card-body flex flex-col gap-400">
        <div className="flex items-center justify-between gap-300">
          <h2 className="certification-heading header-s-medium">
            {t("certification.module.check.heading")}
          </h2>

          <RequirementBadge isRequired />
        </div>

        <dl className="module-stats flex flex-wrap gap-500">
          {stats.map((stat) => (
            <div key={stat.key} className="flex flex-col gap-50">
              <dt className="module-stat-label body-xs-regular">
                {stat.label}
              </dt>

              <dd className="certification-heading body-m-medium">
                {stat.value}
              </dd>
            </div>
          ))}

          <div className="flex flex-col gap-50">
            <dt className="module-stat-label body-xs-regular">
              {t("certification.module.check.status")}
            </dt>

            <dd>
              {isStatusTag ? (
                <span
                  className={`module-status-tag body-xs-regular module-status-tag-${state === KNOWLEDGE_CHECK_STATES.PASSED ? "success" : "warning"}`}
                >
                  {t(`certification.module.check.states.${state}`)}
                </span>
              ) : (
                <span className="certification-muted body-m-medium">
                  {t(`certification.module.check.states.${state}`)}
                </span>
              )}
            </dd>
          </div>
        </dl>

        {state === KNOWLEDGE_CHECK_STATES.UNDER_REVIEW && (
          <div className="flex flex-col items-center gap-200">
            <span className="module-disc flex items-center justify-center">
              <Clock size={ICON_SIZE} />
            </span>

            <h3 className="certification-heading header-s-medium">
              {t("certification.module.check.underReviewTitle")}
            </h3>

            <p className="module-body-text body-s-regular">
              {t("certification.module.check.underReviewBody")}
            </p>
          </div>
        )}

        {state === KNOWLEDGE_CHECK_STATES.PASSED && (
          <p className="module-body-text flex items-center gap-200 body-s-regular">
            <Check size={ICON_SIZE} className="module-success-icon flex-none" />

            {t("certification.module.check.passedBody", {
              position: weekPosition,
            })}
          </p>
        )}

        {state === KNOWLEDGE_CHECK_STATES.ANOTHER_ROUND && (
          <div className="flex flex-col gap-200">
            <p className="module-body-text body-s-regular">
              {incorrect.length > 0
                ? t("certification.module.check.incorrect", {
                    count: incorrect.length,
                    list: new Intl.ListFormat(i18n.language, {
                      type: "conjunction",
                    }).format(incorrect.map(String)),
                  })
                : t("certification.module.check.anotherRoundLead")}
            </p>

            <p className="module-body-text body-s-regular">
              {t("certification.module.check.anotherRoundBody")}
            </p>
          </div>
        )}

        {state === KNOWLEDGE_CHECK_STATES.FAILED && (
          <p className="module-body-text body-s-regular">
            {t("certification.module.check.failedBody", {
              max: check.maxAttempts,
            })}
          </p>
        )}

        {!isOpen &&
          (state === KNOWLEDGE_CHECK_STATES.NOT_STARTED ||
            state === KNOWLEDGE_CHECK_STATES.ANOTHER_ROUND) && (
            <p className="certification-muted body-xs-regular">
              {t(
                isLocked
                  ? "certification.module.check.locked"
                  : "certification.module.check.closed",
              )}
            </p>
          )}

        {(canStart || canRetake) && (
          <div className="flex flex-wrap items-center gap-300">
            <BaseButton onClick={() => setIsModalOpen(true)}>
              {canRetake
                ? t("certification.module.check.startAgain")
                : t("certification.module.check.start")}
            </BaseButton>

            {canRetake && (
              <span className="certification-muted body-xs-regular">
                {t("certification.module.check.startAgainNote")}
              </span>
            )}
          </div>
        )}
      </div>

      <KnowledgeCheckModal
        open={isModalOpen}
        certificationSlug={certificationSlug}
        weekPosition={weekPosition}
        check={check}
        previousAttempt={canRetake ? latest : null}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default KnowledgeCheckCard;
